import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Swal from 'sweetalert2';
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import { useNavigate } from 'react-router-dom';
import Pagination from '../components/Pagination';
import { findIndexById, findPageNumber, deleteItem, SortIcon, searchArray } from '../components/Navigation';
import { Loader } from '../components/ModalsCommonFunctions';

const AcademicYear = () => {
    const authorizedAxios = generateAxiosInstance()
    const [yearName, setYearName] = useState('')
    const [academicYears, setAcademicYears] = useState([])
    const [editId, setEditId] = useState(null)
    const navigate = useNavigate();

    const [startIndex, setStartIndex] = useState(null);
    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);
    const [refresh, setRefresh] = useState(true);

    const [yearSort, setYearSort] = useState(null);
    const [dateSort, setDateSort] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = ["yearName", "createdAt"];

    const [loading, setLoading] = useState(false);
    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            setRefresh(!refresh);
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            setCurrPage(1);
            setAcademicYears(await searchArray("A", '/get-academic-year', searchKeys, searchValue.trim()))
        }
    }

    useEffect(() => {
        getAcademicYear();
    }, [refresh]);

    const getAcademicYear = () => {
        const apiUrl = `/get-academic-year`;
        authorizedAxios && authorizedAxios.get(apiUrl)
            .then(response => {
                setAcademicYears(response.data.result)
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(academicYears, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId])

    useEffect(() => {
        setTotalPages(Math.ceil(academicYears.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > academicYears.length) {
            endIndex = academicYears.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(academicYears.slice(startIndex, endIndex));
    }, [academicYears, currPage, itemsPerPage]);

    const [error, setError] = useState("")

    useEffect(() => {
        if (yearName !== '') {
            setError('')
        }
    }, [yearName])

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        const formattedValue = newValue.replace(/[^0-9-]/g, '');

        // Allow only one dash in the string
        const parts = formattedValue.split('-');
        if (parts.length > 2) {
            setYearName(parts[0] + '-' + parts.slice(1).join(''));
        } else {
            setYearName(formattedValue);
        }
    }

    const addAcademicYear = () => {
        const apiUrl = `/add-academic-year`;
        if (yearName !== "") {
            setLoading(true);
            authorizedAxios && authorizedAxios.post(apiUrl, {
                yearName,
            })
                .then(response => {
                    setAcademicYears((prevSyllabusTypes) => [...prevSyllabusTypes, response.data.result]);
                    setYearName('')
                    setRefresh(!refresh);
                    setSaveId(response.data.result.id);
                    setYearSort(null);
                    setDateSort(null);
                    setSearchValue(null);

                    Swal.fire(
                        { text: response.data.message }
                    );
                })
                .catch(error => {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    Swal.fire(
                        { text: errorMsg }
                    );
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        } else {
            setError("This field is required.")
        }
    }

    const deleteAcademicYear = (id) => {
        let confirmed = window.confirm('Are you sure? you want to delete this record.');
        if (confirmed) {
            setLoading(true);
            const apiUrl = `/delete-academic-year?id=${id}`;
            authorizedAxios && authorizedAxios.delete(apiUrl)
                .then(response => {
                    const page = deleteItem(currPage, itemsPerPage, academicYears.length);
                    setRefresh(!refresh);
                    setCurrPage(page);

                    Swal.fire(
                        { text: response.data.message }
                    );
                })
                .catch(error => {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    Swal.fire(
                        { text: errorMsg }
                    );
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        }
    }

    const updateAcademicYear = () => {
        const apiUrl = `/edit-academic-year?id=${editId}`;
        if (yearName !== "") {
            setLoading(true);
            authorizedAxios && authorizedAxios.put(apiUrl, {
                yearName,
            })
                .then(response => {
                    setAcademicYears((prevData) => [...prevData].map((data) => data.id == editId ? response.data.result : data));
                    setEditId(null);
                    setYearName('')
                    Swal.fire(
                        { text: response.data.message }
                    );
                })
                .catch(error => {
                    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                    Swal.fire(
                        { text: errorMsg }
                    );
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        } else {
            setError("This field is required.")
        }
    }

    useEffect(() => {
        if (yearSort == true || yearSort == false) {
            setDateSort(null);
        }
    }, [yearSort])

    useEffect(() => {
        if (dateSort == true || dateSort == false) {
            setYearSort(null);
        }
    }, [dateSort])

    const handleInputReset = () => {
        setYearName('');
        setEditId('');
        setError("")
        setDateSort(null);
        setYearSort(null);
    }

    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <div id='scroll-up'></div>
                <Header />
                <div className="container-fluid">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb my-0 ms-2">
                            <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                                <span>Dashboard</span>
                            </li>
                            <li className="breadcrumb-item active"><span>Academic Year</span></li>
                        </ol>
                    </nav>
                </div>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fa fa-calendar"></i> Academic Year</b></h4>
                                </div>
                                <div className="card-body ">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">

                                                <div className="col-md-12 board_name">
                                                    <label for="yearName" className="col-form-label"><span style={{ color: "red" }}>*</span>Enter Academic Year</label>
                                                    <input type="text" className="form-control" id="yearName" value={yearName} onChange={handleInputChange} />
                                                    {error && <p style={{ color: 'red' }}>{error}</p>}
                                                </div>

                                                <div className="col-md-12 mt-3 d-flex justify-content-center button-container">
                                                    {!editId && <button className="btn btn-primary" type="button" onClick={addAcademicYear}>Save</button>
                                                    }
                                                    <button className="btn btn-danger delete_icon delete_icon" type="button" onClick={handleInputReset}>Reset</button>
                                                    {
                                                        editId && (<button className="btn btn-success approve_icon" type="button" onClick={updateAcademicYear} disabled={!editId}>Update</button>)
                                                    }
                                                </div>

                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table">
                                                        <div className='search_btn_row'>
                                                            <input
                                                                className="form-control mr-sm-2"
                                                                type="Search"
                                                                placeholder="Search"
                                                                value={searchValue}
                                                                onChange={handleEmptySearch}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        handleSearch();
                                                                    }
                                                                }}
                                                            />
                                                            <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                                                        </div>
                                                        <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                            <table class="table">
                                                                <thead>
                                                                    <tr>
                                                                        <th scope="col">Sr. No.</th>
                                                                        <th scope="col">
                                                                            Academic Year
                                                                            <SortIcon sortState={yearSort} setSortState={setYearSort} setData={setAcademicYears} setCurrPage={setCurrPage} sortingPath={"yearName"} />
                                                                        </th>
                                                                        <th scope="col">
                                                                            Date
                                                                            <SortIcon sortState={dateSort} setSortState={setDateSort} setData={setAcademicYears} setCurrPage={setCurrPage} sortingPath={"createdAt"} />
                                                                        </th>
                                                                        <th scope="col">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {
                                                                        displayedData.length > 0 ?
                                                                            displayedData.map((data, index) => {
                                                                                return (
                                                                                    <tr>
                                                                                        <th scope="row">{startIndex + index + 1}</th>
                                                                                        <td>{data.yearName}</td>
                                                                                        <td>{data.createdAt.split(",")[0]}</td>
                                                                                        <td>
                                                                                            <button className="btn btn-outline-warning edit_icon" type="button" onClick={() => {
                                                                                                setYearName(data.yearName);
                                                                                                setEditId(data.id);
                                                                                                document.getElementById('scroll-up').scrollIntoView({ behavior: 'smooth' });
                                                                                            }}><i class="fa-light fa-pen-to-square"></i></button>
                                                                                            <button className="btn btn-outline-danger delete_icon" type="button" disabled={editId === data.id} onClick={() => deleteAcademicYear(data.id)}> <i class="fa-light fa-trash-can"></i></button>

                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })
                                                                            :
                                                                            (<tr>
                                                                                <th colspan={"100%"} style={{ textAlign: 'center' }}>No Syllabus Types Available</th>

                                                                            </tr>)
                                                                    }
                                                                </tbody>
                                                            </table>

                                                            <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={academicYears.length} />

                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default AcademicYear;